import safeType from '~/utils/safeType';
import { getMemberProducts } from '~/utils/product';
import { useProduct } from '~/modules/product/composables/useProduct';
import type { Detail } from '~~/shared/globalTypes/product';

function getProcessedProductDescription(detailData: Detail) {
  if (detailData.processedProdDesc) {
    return {
      productDetails: detailData.processedProdDesc.productDetails,
      sizeAndFit: detailData.processedProdDesc.sizeAndFit,
      materialsAndCare: detailData.materialsAndCare || detailData.processedProdDesc.fabricAndCare,
    };
  }
  return {
    productDetails: [],
    sizeAndFit: [],
    materialsAndCare: detailData.materialsAndCare || [],
  };
}

export function useProductDetails(detailData: Detail) {
  return {
    productName: safeType(detailData.name, ''),
    productExclusiveName: detailData.exclusiveName || '',
    productDescription: detailData.description || '',
    secondaryDescription: detailData.secondaryDescription || '',
    brandName: detailData.brand?.name || '',
    brandDisplayName: detailData.brand?.subBrand || detailData.brand?.name || '',
    brandUrl: detailData.brand?.url || '',
    bulletText: detailData.bulletText || [],
    bulletLinks: detailData.bulletLinks || [],
    dimensionsBulletText: detailData.dimensionsBulletText || [],
    dimensionsHeaderText: detailData.dimensionsHeaderText || '',
    processedProdDesc: getProcessedProductDescription(detailData),
    numberOfColors: detailData.numberOfColors || 0,
    typeName: detailData.typeName || '',
    swatchEmail: detailData.swatchEmail || '',
    metric: detailData.metric || {
      productUnitSalesCount: '',
      productUnitSalesCountMessage: '',
    },
    pdfEmailDescription: safeType(detailData.pdfEmailDescription, ''),
    highlights: detailData.highlights || [],
  };
}

/**
 * Computes shipping details based on the provided data.
 *
 * @param {Shipping} shippingData
 * @returns {{
 * isShippingAvailable: boolean,
 * freeShippingMessages: Shipping['freeShippingMessages'],
 * notes: Shipping['notes'],
 * returnConstraintMessage: Shipping['returnConstraintMessage'],
 * stateRestrictionMessage: Shipping['stateRestrictionMessage'],
 * shippingMessage: Shipping['shippingMessage'],
 * specialGiftWrapMessage: Shipping['specialGiftWrapMessage'],
 * giftWrapMessage: Shipping['giftWrapMessage'],
 * surchargeMessage: Shipping['surchargeMessage'],
 * marketPlaceProductShippingMessages: Shipping['marketPlaceProductShippingMessages'],
 * bigTicketDeliveryMethods: Shipping['bigTicketDeliveryMethods'],
 * }}
 */
export function useShippingDetails(shippingData) {
  return {
    isShippingAvailable: Object.keys(shippingData).length > 0,
    notes: shippingData.notes || [],
    freeShippingMessages: shippingData.freeShippingMessages || [],
    marketPlaceProductShippingMessages: shippingData.marketPlaceProductShippingMessages || [],
    returnConstraintMessage: shippingData.returnConstraintMessage || '',
    stateRestrictionMessage: shippingData.stateRestrictionMessage || '',
    giftWrapMessage: shippingData.giftWrapMessage || '',
    specialGiftWrapMessage: shippingData.specialGiftWrapMessage || '',
    surchargeMessage: shippingData.surchargeMessage || '',
    shippingMessage: shippingData.shippingMessage || '',
    bigTicketDeliveryMethods: shippingData.bigTicketDeliveryMethods || [],
  };
}

/**
 * Computes relationships details based on the provided data.
 *
 * @param {Relationships} relationshipsData
 * @returns {{
 * taxonomy: import('vue').ComputedRef<Relationships['taxonomy']>,
 * }}
 */
export function useRelationshipsDetails(relationshipsData) {
  return {
    taxonomy: relationshipsData.taxonomy || {},
  };
}

/**
 * Computes pricing badges details based on the provided data.
 *
 * @param {Pricing} pricingData
 * @returns {{
 * badgesMap: import('vue').ComputedRef<Pricing['badgesMap']>,
 * }}
 */
export function usePricingBadgesDetails(pricingData) {
  return {
    badgesMap: pricingData.badgesMap || {},
  };
}

/**
 * @param {Product} [product]
 * @returns {{ productUrl: string, topLevelCategoryID: string, topLevelCategoryName: string }}
 */
export function useIdentifier(product) {
  if (product) {
    return {
      productUrl: safeType(product?.identifier?.productUrl, ''),
      topLevelCategoryID: safeType(product?.identifier?.topLevelCategoryID, ''),
      topLevelCategoryName: safeType(product?.identifier?.topLevelCategoryName, ''),
    };
  }
  return {
    productUrl: '',
    topLevelCategoryID: '',
    topLevelCategoryName: '',
  };
}

export function useProductGetters() {
  const { productData, productId } = useProduct();
  const isProductAvailable = !!productData?.availability?.available;
  const isCcfpAvailable = !!productData?.ccfp?.ccfpEligible;

  const productDetails = useProductDetails(productData?.detail || {});
  const shippingDetails = useShippingDetails(productData?.shipping || {});
  const relationshipsDetails = useRelationshipsDetails(productData?.relationships || {});
  const pricingBadgesDetails = usePricingBadgesDetails(productData?.pricing || {});
  const identifier = useIdentifier(productData);
  const bannerDetails = productData?.banner || {};
  const isSingleUpcProduct = Object.keys(productData?.traits?.traitsMaps?.upcMap || {}).length === 1;
  /**
   * @param {(value: Product, index: number, array: any[]) => unknown} cb
   * @returns {boolean}
   */
  function anyMemberProducts(cb) {
    return getMemberProducts(productData).some(cb);
  }

  return {
    productId,
    isProductAvailable,
    isCcfpAvailable,
    bannerDetails,
    ...identifier,
    ...productDetails,
    ...shippingDetails,
    ...relationshipsDetails,
    ...pricingBadgesDetails,
    anyMemberProducts,
    isSingleUpcProduct,
  };
}

export default useProductGetters;
